import {IconButton} from '@wandb/components';
import {
  applyFilter,
  getFilterFromBBoxConfig,
} from '@wandb/weave/common/util/PointCloudFiltering';
import {
  type BabylonPointCloud,
  type RenderFullscreen,
  type RenderScreenshot,
  type RgbaColor,
  type RgbColor,
} from '@wandb/weave/common/util/render_babylon';
import {
  getFilteringOptionsForPointCloud,
  loadPointCloud,
} from '@wandb/weave/common/util/SdkPointCloudToBabylon';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import * as Analytics from '../services/analytics/productEvents';
import {loadBoxData} from '../state/media/actions';
import {DD_PRIVACY_HIDDEN} from '../util/datadog/privacy';
import {
  type MediaPanelCardControl,
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from './MediaCard';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';

export interface Media3DProps {
  width: number;
  height: number;
  url: string;
  blob: Blob;
  controls?: MediaPanelCardControl;
  mediaKey: string;
  mediaPanelRefId: string;
  backgroundColor?: RgbaColor;
  pointColor?: RgbColor;
  useRightHandedSystem?: boolean;
  tileMedia: TileMedia | null;
  tileMediaSpec: TileMediaSpec | undefined;
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null;
}

export const PointCloud = ({
  width,
  height,
  blob,
  mediaKey,
  mediaPanelRefId,
  controls,
  backgroundColor,
  pointColor,
  useRightHandedSystem,
  tileMedia,
  tileMediaSpec,
  getMediaTracking,
}: Media3DProps) => {
  const [renderErrorMessage, setRenderErrorMessage] = useState<string>();
  const {pointCloud, loadErrorMessage} = usePointCloud(
    blob,
    mediaKey,
    mediaPanelRefId,
    controls,
    pointColor
  );

  const {trackSuccess, trackError} = useGetTrackingCallbacks(
    tileMedia,
    tileMediaSpec,
    getMediaTracking
  );

  const babylonContainerRef = useRef<HTMLDivElement>(null);

  // Load babylon lib asynchronously to perform bundle splitting
  type BabylonLib = typeof import('@wandb/weave/common/util/render_babylon');
  const [babylonLib, setBabylon] = useState<BabylonLib>();
  useEffect(() => {
    import('@wandb/weave/common/util/render_babylon').then(setBabylon);
  }, []);

  const [screenshot, setScreenshot] = useState<string>();

  useEffect(() => {
    let cleanup: CallableFunction | undefined;
    const renderScreenshot = async () => {
      if (babylonContainerRef.current && babylonLib && pointCloud) {
        try {
          const result = babylonLib.renderJsonPoints<RenderScreenshot>(
            pointCloud,
            {
              fullscreen: false,
              width,
              height,
            },
            controls?.cameraControl,
            backgroundColor,
            useRightHandedSystem
          );
          cleanup = result.cleanup;
          const img = await babylonLib.renderScreenshot(result);
          trackSuccess?.();
          setScreenshot(img);
        } catch (e) {
          if (e instanceof Error) {
            setRenderErrorMessage(e.message);
          }
          console.error(e);
          trackError?.();
        }
      }
    };

    renderScreenshot();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [
    width,
    height,
    pointCloud,
    babylonLib,
    controls,
    backgroundColor,
    useRightHandedSystem,
    trackSuccess,
    trackError,
  ]);

  useEffect(() => {
    Analytics.pointCloudViewed({type: 'preview'});
  }, []);

  // Callback launch fullscreen viewer
  // NOTE: This has to stay a callback because fullscreen
  // requests can only happen as a response to user actions
  const requestFullscreen = useCallback(() => {
    let cleanup: CallableFunction | undefined;
    const renderFullscreen = async () => {
      if (babylonLib && babylonContainerRef.current && pointCloud) {
        const domElement = babylonContainerRef.current;
        const result = await babylonLib.renderJsonPoints<RenderFullscreen>(
          pointCloud,
          {
            domElement,
            fullscreen: true,
          },
          undefined,
          backgroundColor,
          useRightHandedSystem
        );
        babylonLib.renderFullscreen(result);
        cleanup = result.cleanup;
        Analytics.pointCloudViewed({type: 'fullscreen'});
      }
    };
    renderFullscreen();

    return cleanup;
  }, [pointCloud, babylonLib, backgroundColor, useRightHandedSystem]);

  const errorMessage = renderErrorMessage ?? loadErrorMessage;
  if (errorMessage) {
    console.log(`error ${errorMessage}`);
    return <div className="card3d">Error: {errorMessage}</div>;
  }

  return (
    <div className="media-card group">
      <div className="object3D-card-babylon" ref={babylonContainerRef} />
      {screenshot && (
        <>
          <img
            alt="point-cloud-card"
            data-test="point-cloud-card"
            data-dd-privacy={DD_PRIVACY_HIDDEN}
            src={screenshot}
          />
          {/* oxlint-disable-next-line tailwindcss/no-restricted-classes -- grandfathered :has() usage */}
          <div className="media-card__fullscreen [&:has(:focus-visible)]:!text-white [&_button]:!bg-transparent">
            <IconButton
              aria-label="Open point cloud fullscreen"
              icon="full-screen-mode-expand"
              iconColor="currentcolor"
              onPress={requestFullscreen}
              variant="ghost"
            />
          </div>
        </>
      )}
    </div>
  );
};

const usePointCloud = (
  blob: Blob,
  mediaKey: string,
  mediaPanelRefId: string,
  controls: MediaPanelCardControl | undefined,
  pointColor?: RgbColor
): {
  pointCloud: BabylonPointCloud | undefined;
  loadErrorMessage: string | undefined;
} => {
  const dispatch = useDispatch();
  const [pointCloud, setPointCloud] = useState<BabylonPointCloud>();
  const [rawPointCloud, setRawPointCloud] = useState<BabylonPointCloud>();
  const [loadErrorMessage, setLoadErrorMessage] = useState<string>();

  useEffect(() => {
    // Empty placeholder blobs are used while the media panel download is still
    // in flight; parsing them yields "Unexpected end of JSON input".
    // We should not typically hit here. The component should not reload until the blob is loaded.
    if (blob.size === 0) {
      setRawPointCloud(undefined);
      setLoadErrorMessage(undefined);
      return;
    }

    let cancelled = false;
    // Clear any stale error from a previous blob
    setLoadErrorMessage(undefined);
    new Response(blob)
      .text()
      .then(t => {
        if (cancelled) {
          return;
        }
        try {
          setRawPointCloud(loadPointCloud(t));
        } catch (e) {
          if (e instanceof Error) {
            setLoadErrorMessage(e.message);
          } else {
            setLoadErrorMessage('unknown error');
            console.error('unknown render error:', e);
          }
        }
      })
      .catch(e => {
        if (!cancelled) {
          setLoadErrorMessage(e instanceof Error ? e.message : String(e));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [blob]);

  // Dispatch of pointcloud data and applying the filter run in separate useEffects
  // because we only want to run dispatch after the pointcloud data itself
  // changes.
  useEffect(() => {
    if (rawPointCloud) {
      let newPointCloud = rawPointCloud;
      // If the point color is set in the media settings, then override the point color that was logged.
      if (pointColor) {
        newPointCloud = {
          ...rawPointCloud,
          points: rawPointCloud.points.map(point => ({
            ...point,
            color: pointColor,
          })),
        };
      }
      const {boxData, newClassIdToLabel} =
        getFilteringOptionsForPointCloud(newPointCloud);
      setPointCloud(
        applyFilter(
          newPointCloud,
          getFilterFromBBoxConfig(
            controls?.boundingBoxControl,
            newClassIdToLabel
          )
        )
      );

      // The filtering UI (useBoundingBoxData()) needs a way to know labels the
      // bounding boxes have in order to enable the user to select filtering options.
      // Rather than having it load the point cloud bounding box data files on its own,
      // we put the data we just read from the point cloud file into redux,
      // and then the filter can load it as needed.
      dispatch(
        loadBoxData({
          mediaKey,
          panelID: mediaPanelRefId,
          mediaID: '1', // This value is not currently used by selectors for this data
          boxData,
        })
      );
    }
  }, [
    rawPointCloud,
    dispatch,
    mediaKey,
    mediaPanelRefId,
    controls?.boundingBoxControl,
    pointColor,
  ]);

  return {pointCloud, loadErrorMessage};
};

export default PointCloud;
